<template>
    <div class="app-reg-info-group">
        <div class="app-reg-flex">
            <div class="app-reg-info-child-group">
                <span for="vehicleText" class="vehicleText">
                    {{ regInfo.year }} {{ regInfo.make }} {{ regInfo.model !== 'UNKNOWN' ? regInfo.model : '' }}
                </span>
            </div> 
            <div class="app-reg-info-child-group">
                <label for="vehicleVin">VIN</label>                
                <span for="vehicleVinText">{{ regInfo.vin }}</span>
            </div>
            <div class="app-reg-info-child-group">    
                <label for="vehiclePlateNumber" alt="Vehicle plate label">Plate</label>                
                <span for="vehiclePlateNumberText" v-if="regInfo.plateNumber !== '' || regInfo.plateType !== ''">
                    {{ regInfo.plateNumber }}/{{ regInfo.plateType }}
                    <div class="statusTextFlex" v-if="regInfo.registrationStatus !== ''">
                        <img v-if="regInfo.registrationStatus === 'Active'" class="active-icon" src="/static/status-active.png" alt="Icon">
                        <img v-else class="active-icon" src="/static/status-not-active.png" alt="Icon">
                        <span class="statusText">{{ regInfo.registrationStatus }}</span>
                    </div>
                </span>
            </div>
            <div class="app-reg-info-child-group">
                <label for="vehicleExpirationDate">Expiration date</label>                
                <img v-if="regInfo.expirationDate !='NA' && regInfo.expirationDate !=''" class="app-icon" src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="Icon">
                <span for="vehicleExpirationDateText">{{ regInfo.expirationDate }}</span>
            </div>
            <div class="app-reg-info-child-group subTabCaret" v-if="!regInfo.showDetails">
                <button class="showMoreText" type="button" @click="toggleDetails"
                    @keydown.enter="toggleDetails">
                    {{ regInfo.showDetails ? 'Less' : 'More...' }}
                </button>
            </div>
        </div>
        
        <!-- Inspection Warning -->
        <div class="app-reg-info-child-inspection-group" v-if="isInspectionDueIn30Days(regInfo.inspectionDeadlineDate)">
            <span class="inspectionStatusText">
                Your Vehicle emission inspection is due by <b>{{ regInfo.inspectionDeadlineDate }}</b>; failure to obtain emission inspection certificate by this date will suspend your registration. For more information please refer to <a href="https://dmv.ri.gov/registrations-plates-titles/safety-emissions-inspections/" target="_blank" rel="noopener noreferrer">Safety and Emissions Inspections</a> page.
            </span>
        </div> 
        
        <!-- Plate Shipped Info -->
        <div class="app-reg-info-child-shipped-group" v-if="isPlateShipped(regInfo.shippedDate)">
            <span class="shippedStatusText">
                The plate for this registration has been shipped to you on <b>{{ regInfo.shippedDate }}</b> via <b>{{ regInfo.shippingMethod }}</b>. The tracking # is <b>{{ regInfo.trackingNumber }}</b>.
            </span>
        </div> 
        
        <!-- Details -->
        <div class="app-reg-flex" v-if="regInfo.showDetails">
            <div class="app-reg-info-child-group">
                <label for="vehicleType">Vehicle type</label>                
                <span for="vehicleTypeText">{{ regInfo.vehicleType }}</span>
            </div>
            <div class="app-reg-info-child-group">    
                <label for="vehiclePlateDesign">Plate design</label>                
                <span for="vehiclePlateDesignText">{{ regInfo.plateDesign }}</span>
            </div>
            <div class="app-reg-info-child-group">
                <label for="vehicleRegistrationRenewalFee">Registration renewal fee</label>                
                <span for="vehicleRegistrationRenewalFeeText">{{ regInfo.registrationRenewalFee === 'NA' || regInfo.registrationRenewalFee === '' ? regInfo.registrationRenewalFee : `$ ${regInfo.registrationRenewalFee}` }}</span>
            </div>
            <div class="app-reg-info-child-group">
                <label for="vehicleOwnershipType">Ownership type</label>                
                <span for="vehicleOwnershipTypeText">{{ regInfo.ownershipType }}</span>
            </div>
        </div>
        
        <!-- Details -->
        <div class="app-reg-flex" v-if="regInfo.showDetails">
            <div class="app-reg-info-child-group">
                <label for="vehicleOwnershipDocument">Ownership document</label>                
                <span for="vehicleOwnershipDocumentText">{{ regInfo.ownershipDocument }}</span>
            </div>
            <div class="app-reg-info-child-group">
                <label for="vehicleTitleNumber">Title number</label>                
                <span for="vehicleTitleNumberText">{{ regInfo.titleNumber }}</span>
            </div>  
            <div class="app-reg-info-child-group">
                <label for="vehicleTitleStatus">Status</label>                
                <span for="vehicleTitleStatusText">{{ regInfo.titleStatus }}</span>
            </div>
            <div class="app-reg-info-child-group">
                <label for="vehicleTitleStatus">Is Electronic Title</label>                
                <span for="vehicleTitleStatusText">{{ regInfo.isTitleElectronic }}</span>
            </div>
        </div>
        
        <!-- Details - Lien Information -->
        <div class="app-reg-flex" v-if="regInfo.showDetails">
            <div class="app-reg-info-child-group" v-if="regInfo.firstLienName && regInfo.firstLienName !== ''">
                <label for="vehicleOwnershipType">Lien holder name (first)</label>                
                <span for="vehicleOwnershipTypeText">{{ regInfo.firstLienName }}</span>
            </div>
            <div class="app-reg-info-child-group" v-if="regInfo.firstLienDate && regInfo.firstLienDate !== ''">
                <label for="vehicleOwnershipDocument">Lien date (first)</label>
                <span for="vehicleOwnershipDocumentText">{{ regInfo.firstLienDate }}</span>
            </div>
            <div class="app-reg-info-child-group" v-if="regInfo.secondLienName && regInfo.secondLienName !== ''">
                <label for="vehicleTitleNumber">Lien holder name (second)</label>                
                <span for="vehicleTitleNumberText">{{ regInfo.secondLienName }}</span>
            </div>  
            <div class="app-reg-info-child-group" v-if="regInfo.secondLienDate && regInfo.secondLienDate !== ''">
                <label for="vehicleTitleStatus">Lien date (second)</label>                
                <span for="vehicleTitleStatusText">{{ regInfo.secondLienDate }}</span>
            </div>
        </div>
        
        <!-- Suspensions Header -->
        <div class="app-reg-flex" v-if="regInfo.showDetails && regInfo.suspensions?.length > 0">
            <div class="app-reg-info-child-grid-group">    
                <label for="suspensionDescription">Suspension Description</label>                
            </div>
            <div class="app-reg-info-child-grid-group">
                <label for="suspensionEffectiveDate">Suspension Effective Date</label>    
            </div>
            <div class="app-reg-info-child-grid-group">
                <label for="suspensionStatus">Suspension Status</label> 
            </div>
        </div> 
        
        <!-- Suspensions Data -->
        <div class="app-reg-info-group" v-if="regInfo.showDetails && regInfo.suspensions">
            <div class="app-reg-info-susp-row" v-for="(suspensionInfo, j) in regInfo.suspensions" :key="j">
                <div class="app-reg-flex">
                    <div class="app-reg-info-child-desc-group">    
                        <span for="suspensionDescriptionText">{{ suspensionInfo.suspensionDescription }}</span>
                    </div>
                    <div class="app-reg-info-child-grid-group-text">
                        <span for="suspensionEffectiveDateText">{{ suspensionInfo.suspensionEffectiveDate }}</span>
                    </div>
                    <div class="app-reg-info-child-grid-group-text">
                        <span for="suspensionStatusText">{{ suspensionInfo.suspensionStatus }}</span>
                    </div>
                </div>      
            </div>
        </div>
        
        <!-- Collapse Button -->
        <div class="app-reg-flex" v-if="regInfo.showDetails">
            <div class="app-reg-info-child-group">
                <button class="showMoreText" type="button" @click="toggleDetails" 
                    @keydown.enter="toggleDetails">
                    {{ regInfo.showDetails ? 'Less' : 'More...' }}
                </button>
            </div>
        </div>
        
        <!-- Action Buttons -->
        <div class="app-renew-reg">
            <div v-if="regInfo.isPlateRenewable === 'YES'">
                <button class="renew-reg" type="button" @click="$emit('renew', regInfo)">
                    Renew Registration
                </button>
            </div>
            <div v-if="regInfo.isPlateCancelAllowed === 'YES'">
                <button class="renew-reg" type="button" @click="$emit('cancel', regInfo)">
                    Cancel Registration
                </button>
            </div>
            <div v-if="regInfo.isPlateDuplicateAllowed === 'YES'">
                <button class="renew-reg" type="button" @click="$emit('duplicate', regInfo)">
                    Duplicate Registration
                </button>
            </div>
            <div>
                <button class="renew-reg" type="button" v-if="(regInfo.registrationStatus !== 'Cancelled' 
                && (regInfo.isPlateRenewable === 'YES' || regInfo.isPlateDuplicateAllowed === 'YES' || regInfo.isPlateCancelAllowed === 'YES'))" >
                    <a href="https://www.ri.gov/DMV/plate_remake/" target="_blank" rel="noopener noreferrer" class="renew-reg">Plate Remake/Plate Design Change</a>
                </button>
            </div>
            <div v-if="regInfo.isDuplicateTitleAllowed === 'YES'">
                <button class="renew-reg" type="button" @click="$emit('duplicate-title', regInfo)">
                    Duplicate Title
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
const props = defineProps({
    regInfo: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['renew', 'cancel', 'duplicate', 'duplicate-title', 'toggle-details']);

const toggleDetails = () => {
    emit('toggle-details', props.regInfo);
};

const isInspectionDueIn30Days = (dateValue) => {
    const inspectionDate = new Date(dateValue);
    const maxDate = new Date();
    const timeDifference = inspectionDate.getTime() - maxDate.getTime();
    const dateDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
    return dateDifference < 31 && dateDifference >= 0;
};

const isPlateShipped = (dateValue) => {
    if (dateValue === '') return false;
    const shippedDate = new Date(dateValue);
    const maxDate = new Date();
    const timeDifference = maxDate.getTime() - shippedDate.getTime();
    const dateDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
    return dateDifference < 91 && dateDifference >= 0;
};
</script>

<style scoped lang="scss">
.app-reg-flex {
    display: flex;
    gap: 0rem 2rem;
    flex-wrap: wrap;
    
    .app-reg-info-child-group {
        width: 240px;
        padding: 1rem;
        
        .app-icon {
            margin-right: 5px;
            filter: invert(0%) sepia(0%) saturate(7500%) hue-rotate(143deg) brightness(108%) contrast(106%);
            vertical-align: text-bottom;
        }
        
        .vehicleText {
            font-size: 1.2rem;
            font-weight: 700;
        }
        
        .statusText {
            margin: 2px;
        }
        
        .statusTextFlex {
            display: flex;
        }
    }

    .app-reg-info-child-desc-group {
        width: 240px;
        padding-left: 1rem;
    }
    
    .app-reg-info-child-grid-group-text {
        width: 240px;
        padding-left: 1rem;
    }
    
    .app-reg-info-child-grid-group {
        width: 240px;
        padding-left: 1rem;
        padding-top: 1rem;
    }
}

.app-reg-info-child-inspection-group {
    padding-left: 1rem;
    padding-right: 1rem;
    
    .inspectionStatusText {
        margin: 2px;        
    }
}

.app-reg-info-child-shipped-group {
    margin: 1rem;
}

.app-reg-info-susp-row {
    display: flex;
    margin: 0px 0px 10px 0px;
    align-items: center;
    gap: 4rem; 
}

.active-icon {
    width: 25px;
    height: 25px;
    vertical-align: middle;
}

.subTabCaret {
    margin-top: 0.7rem;
    position: absolute;
    right: 10%;
}

.showMoreText {
    cursor: pointer;
}

.app-renew-reg {
    display: flex;
    flex-wrap: wrap;
    text-align: center;          
    justify-content: center;  
    padding-left: 0.5rem;
    gap: 0rem 2rem;
    .renew-reg {
        color: var(--ri-blue);
        cursor: pointer;
        text-decoration: underline;
        border: none;
        border-radius: 0px;
        background-color: transparent;
        font-size: 16px;
        padding: 0px 0px;
        text-align: left;
        justify-content: center;
        font-weight: 100;
        padding-left: 2px;
        margin-top: 0.5rem;
        
        &:hover {
            text-decoration: none;
        }
    }
}

@media only screen and (max-width: 726px) {
    .app-reg-flex .app-reg-info-child-group {
        min-width: 155px;
    }
    
    .app-reg-info-child-group {
        span {
            word-wrap: break-word;
        }
    }
    
    .subTabCaret {
        width: 120px;
        position: static;
    }
    
    .app-renew-reg {
        justify-content: flex-start;
        padding-left: 1rem;
    }
}

@media only screen and (min-width: 727px) {
    .app-reg-flex .app-reg-info-child-group {
        width: 250px;
    }
    
    .subTabCaret {
        position: static;
    }
}
</style>